import React, { useMemo } from 'react';
import styled from 'styled-components';
import { ButtonBase, Button, Box, Slide, Tab } from '@material-ui/core';
import {
  Theme,
  createStyles,
  withStyles,
  createTheme,
  ThemeOptions,
} from '@material-ui/core/styles';
import { TransitionProps } from '@material-ui/core/transitions';
import { Colors } from 'typings';

// add custom background colors
declare module '@material-ui/core/styles/createPalette' {
  interface TypeBackground {
    custom?: React.CSSProperties['color'];
    alternative?: React.CSSProperties['color'];
    disabled?: React.CSSProperties['color'];
    green?: React.CSSProperties['color'];
  }
  interface TypeBackgroundOptions {
    custom?: React.CSSProperties['color'];
    alternative?: React.CSSProperties['color'];
    disabled?: React.CSSProperties['color'];
    green?: React.CSSProperties['color'];
  }
}

interface StyledIconButtonProps {
  color?: string;
}

interface StyledTabProps {
  label: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: any;
  value: any;
}

export const muiThemeObject: ThemeOptions = {
  palette: {
    primary: {
      main: '#2196f3',
    },
    secondary: {
      main: '#E91E63',
    },
  },
  spacing: (factor: number) => `${0.5 * factor}rem`,
  typography: {
    fontFamily: 'MarkOffcPro, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto',
  },
};

export const useConsoleTheme = (darkMode: boolean) => {
  const consoleTheme = useMemo(
    () =>
      createTheme({
        ...muiThemeObject,
        palette: {
          ...muiThemeObject.palette,
          type: darkMode ? 'dark' : 'light',
          primary: {
            main: darkMode ? '#197ccb' : '#2196f3',
          },
          secondary: {
            main: darkMode ? '#c51953' : '#E91E63',
          },
          background: {
            paper: darkMode ? '#555555' : '#EFEFEF',
            custom: darkMode ? '#737373' : '#FFFFFF',
            alternative: darkMode ? '#4c4c4c' : '#F5F5F5',
            disabled: darkMode ? '#303030' : '#B5B5B5BF',
            green: darkMode ? '#24b763' : '#29D773',
          },
          text: {
            primary: darkMode ? '#FFFFFF' : '#000000',
          },
        },
        props: {
          MuiButtonBase: {
            disableRipple: true,
          },
        },
        overrides: {
          MuiDialog: {
            paper: {
              // have dialog use the material-ui paper component default styles
              backgroundColor: darkMode ? '#303030' : '#FAFAFA',
            },
          },
          MuiSelect: {
            selectMenu: {
              paddingLeft: '0.5rem',
            },
          },
        },
      }),
    [darkMode]
  );

  return consoleTheme;
};

export const StyledIconButton = styled(ButtonBase).attrs<StyledIconButtonProps>(
  {}
)<StyledIconButtonProps>`
  &.MuiButtonBase-root {
    color: ${(props) => props.color || Colors.WHITE};
  }
`;

export const ButtonPill = styled(Button)`
  &.MuiButton-root {
    background-color: ${(props) => props.theme.palette.primary.main};
    color: ${Colors.WHITE};
    text-transform: none;
    font-size: var(--s-2);
    box-shadow: 0px 3px 6px ${Colors.TRANSPARENT_BLACK};
    border-radius: 50px;
    padding-left: ${(props) => props.theme.spacing(2)};
    padding-right: ${(props) => props.theme.spacing(2)};

    &:hover {
      background-color: ${Colors.DARK_BLUE};
    }

    svg {
      fill: ${Colors.WHITE};
    }
  }

  &.Mui-disabled {
    background-color: ${Colors.LIGHT_GRAY};
  }
`;

export const StyledTab = withStyles((theme: Theme) =>
  createStyles({
    root: {
      textTransform: 'none',
      border: `1px solid ${Colors.WHITE_ISH}`,
      fontSize: theme.typography.pxToRem(15),
      opacity: 0.5,
      marginRight: theme.spacing(1),
      borderRadius: theme.spacing(1, 1, 0, 0),
      '&:focus': {
        opacity: 1,
        color: theme.palette.primary.main,
        borderBottom: 'none',
      },
      '&$selected': {
        borderBottom: 'none',
        color: theme.palette.primary.main,
      },
    },
    selected: {},
  })
)((props: StyledTabProps & React.HTMLAttributes<any>) => <Tab disableRipple {...props} />);

export const TabPanel = (props: TabPanelProps & React.HTMLAttributes<any>) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabPanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && <Box className="no-padding">{children}</Box>}
    </div>
  );
};

export const SlideTransition = React.forwardRef(function Transition(
  props: TransitionProps & { children?: React.ReactElement<any, any> },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});
